import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Settings2,
  Eye,
  EyeOff,
  GripVertical,
  RotateCcw,
  Save,
  Trash2,
  MoreVertical,
  Check,
  Search,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FieldConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
  category?: string;
  pinned?: boolean;
}

export interface FieldPreset {
  id: string;
  name: string;
  fields: FieldConfig[];
  isDefault?: boolean;
}

interface FieldSelectorProps {
  fields: FieldConfig[];
  defaultFields?: FieldConfig[]; // The original default configuration
  onFieldsChange: (fields: FieldConfig[]) => void;
  storageKey?: string;
  presets?: FieldPreset[];
  onSavePreset?: (preset: FieldPreset) => void;
  onDeletePreset?: (presetId: string) => void;
}

interface SortableFieldItemProps {
  field: FieldConfig;
  onToggleVisibility: (id: string) => void;
}

function SortableFieldItem({ field, onToggleVisibility }: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-2 rounded-lg border bg-card transition-all',
        isDragging && 'opacity-50 shadow-lg',
        field.visible ? 'border-primary/20 bg-primary/5' : 'border-border'
      )}
    >
      {/* Drag Handle */}
      <button
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Field Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn(
            'text-sm font-medium truncate',
            !field.visible && 'text-muted-foreground'
          )}>
            {field.label}
          </span>
          {field.category && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5">
              {field.category}
            </Badge>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onToggleVisibility(field.id)}
        >
          {field.visible ? (
            <Eye className="h-4 w-4 text-primary" />
          ) : (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>
    </div>
  );
}

export function FieldSelector({
  fields: initialFields,
  defaultFields,
  onFieldsChange,
  storageKey,
  presets = [],
  onSavePreset,
  onDeletePreset,
}: FieldSelectorProps) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState<FieldConfig[]>(initialFields);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [deletePresetId, setDeletePresetId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load saved configuration
  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(`field-config-${storageKey}`);
      if (saved) {
        try {
          const savedFields = JSON.parse(saved);
          setFields(savedFields);
        } catch (e) {
          console.error('Failed to load field configuration:', e);
        }
      }
    }
  }, [storageKey]);

  // Update local state only (don't apply to grid yet)
  const updateLocalFields = (newFields: FieldConfig[]) => {
    setFields(newFields);
  };

  // Apply changes to the grid and save to localStorage
  const applyChanges = () => {
    onFieldsChange(fields);
    if (storageKey) {
      localStorage.setItem(`field-config-${storageKey}`, JSON.stringify(fields));
    }
    setOpen(false);
  };

  // Reset to initial fields when drawer opens
  useEffect(() => {
    if (open) {
      setFields(initialFields);
    }
  }, [open, initialFields]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.id === active.id);
      const newIndex = fields.findIndex((f) => f.id === over.id);

      const newFields = arrayMove(fields, oldIndex, newIndex).map((field: FieldConfig, index: number) => ({
        ...field,
        order: index,
      }));

      updateLocalFields(newFields);
    }
  };

  const toggleVisibility = (id: string) => {
    const newFields = fields.map((field) =>
      field.id === id ? { ...field, visible: !field.visible } : field
    );
    updateLocalFields(newFields);
  };

  const showAll = () => {
    const newFields = fields.map((field) => ({ ...field, visible: true }));
    updateLocalFields(newFields);
  };

  const resetToDefault = () => {
    // Use the provided defaultFields if available, otherwise use initialFields
    const fieldsToReset = defaultFields || initialFields;
    const resetFields = fieldsToReset.map((field, index) => ({
      ...field,
      visible: true,
      order: index,
      pinned: false,
    }));
    updateLocalFields(resetFields);
  };

  const applyPreset = (preset: FieldPreset) => {
    updateLocalFields(preset.fields);
  };

  const handleSavePreset = () => {
    if (presetName.trim() && onSavePreset) {
      const preset: FieldPreset = {
        id: `preset-${Date.now()}`,
        name: presetName.trim(),
        fields: [...fields],
      };
      onSavePreset(preset);
      setPresetName('');
      setShowSaveDialog(false);
    }
  };

  const handleDeletePreset = () => {
    if (deletePresetId && onDeletePreset) {
      onDeletePreset(deletePresetId);
      setDeletePresetId(null);
    }
  };

  const filteredFields = fields.filter((field) =>
    field.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const visibleCount = fields.filter((f) => f.visible).length;
  const totalCount = fields.length;

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-9 p-0">
            <Settings2 className="h-4 w-4" />
          </Button>
        </SheetTrigger>
        <SheetContent className="w-[450px] sm:w-[450px] sm:max-w-none px-6 flex flex-col gap-2">
          <SheetHeader className="flex-shrink-0 p-0 pt-4 gap-1">
            <SheetTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-primary" />
              Configure Columns
            </SheetTitle>
          </SheetHeader>

          {/* Fixed Top Section - Quick Actions, Presets, Search */}
          <div className="space-y-2 pt-2 flex-shrink-0">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={showAll}
                className="gap-2"
              >
                <Eye className="h-4 w-4" />
                View All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={resetToDefault}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset Default
              </Button>
            </div>

            {/* Presets */}
            {presets.length > 0 && (
              <>
                <Separator />
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Saved Presets</Label>
                  <div className="flex flex-wrap gap-2">
                    {presets.map((preset) => (
                      <div key={preset.id} className="flex items-center gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => applyPreset(preset)}
                          className="gap-2"
                        >
                          {preset.isDefault && <Check className="h-3 w-3" />}
                          {preset.name}
                        </Button>
                        {!preset.isDefault && onDeletePreset && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => setDeletePresetId(preset.id)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search fields..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Scrollable Field List Section */}
          <div className="flex-1 overflow-y-auto min-h-0 pt-2 pr-2">
            {/* Field List */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Fields ({visibleCount} visible)
                </Label>
                <span className="text-xs text-muted-foreground">
                  Drag to reorder
                </span>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredFields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1.5">
                    {filteredFields.map((field) => (
                      <SortableFieldItem
                        key={field.id}
                        field={field}
                        onToggleVisibility={toggleVisibility}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>

          <SheetFooter className="flex-row gap-2 sm:gap-2 flex-shrink-0 mt-0 p-0 pt-2 pb-2">
            {onSavePreset && (
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(true)}
                className="flex-1 gap-2"
              >
                <Save className="h-4 w-4" />
                Save Preset
              </Button>
            )}
            <Button
              onClick={applyChanges}
              className="flex-1"
            >
              Apply Changes
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Save Preset Dialog */}
      <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Field Configuration</AlertDialogTitle>
            <AlertDialogDescription>
              Give your field configuration a name to save it as a preset for future use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="preset-name">Preset Name</Label>
            <Input
              id="preset-name"
              placeholder="e.g., Manager View, Detailed Report"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSavePreset();
                }
              }}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
            >
              Save Preset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Preset Dialog */}
      <AlertDialog open={!!deletePresetId} onOpenChange={() => setDeletePresetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this preset? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePreset}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
